import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TablePagination from '@material-ui/core/TablePagination';
import classNames from 'classnames';
import Paper from '@material-ui/core/Paper';
import Toolbar from '@material-ui/core/Toolbar';
import { useToggleNavBar, useLoadingBar } from '../../libraries/contexts';
import TablePaginationActionsWrapped from './pagination';
import EmptyTable from './emptyTable';
import EnhancedTableHead from './head';
import ContentTable from './content';
import Search from './search';
import { AddButton } from './buttons';

import {
  ROWS_PER_PAGE_DEFAULT,
  ROWS_PER_PAGE_OPTIONS,
  INITIAL_PAGE_DEFAULT
} from './../../libraries/constants';
import { EnhancedTableStyles as styles } from './styles';

const EnhancedTable = props => {
  const { service, classes, search, history, name } = props;
  const [toggleValue] = useToggleNavBar();
  const [loading, setLoading] = useLoadingBar();
  const { isAdvancedDateFilter = true } = service;
  const [data, setData] = useState([]);
  const [order, setOrder] = useState('');
  const [orderBy, setOrderBy] = useState('');
  const [count, setCount] = useState(INITIAL_PAGE_DEFAULT);
  const [selected, setSelected] = useState([]);
  const [filteredData, setFilteredData] = useState('');
  const [page, setPage] = useState(INITIAL_PAGE_DEFAULT);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_DEFAULT);
  const key = service.getKeyTable();

  const getRequests = async () => {
    setLoading({ principalLoading: true });
    let data = await service.getPaginated(order, orderBy, page + 1, rowsPerPage, filteredData);
    if (data) {
      const { results, count } = data;
      setData(service.makeDataTable(results));
      setCount(count);
      setLoading({ principalLoading: false });
    }
  };

  useEffect(() => {
    getRequests();
  }, []);

  useEffect(() => {
    getRequests();
  }, [toggleValue.message.open]);

  useEffect(() => {
    getRequests();
  }, [filteredData, page, rowsPerPage]);

  const handleChangePage = (event, page) => {
    setPage(page);
  };

  const handleChangeRowsPerPage = event => {
    const {
      target: { value }
    } = event;
    setPage(0);
    setRowsPerPage(value);
  };

  const handleSelectAllClick = event => {
    const {
      target: { checked }
    } = event;

    const newSelected = checked ? data.map(n => n.id) : [];
    setSelected(newSelected);
  };

  const handleRequestSort = (event, property) => {
    let newOrder = '';
    if (orderBy === property && order === '') {
      newOrder = '-';
    }
    setOrder(newOrder);
    setOrderBy(property);
  };

  return (
    <Paper className={classes.root}>
      <Toolbar className={classNames(classes.root, classes.toolbar)}>
        <Search
          setFilteredData={setFilteredData}
          search={search}
          isAdvancedDateFilter={isAdvancedDateFilter}
        />
        {service.hasAddOption ? <AddButton {...props} /> : ''}
      </Toolbar>
      <div className={classes.tableWrapper}>
        <Table className={classes.table} aria-labelledby="tableTitle">
          <EnhancedTableHead
            numSelected={selected.length}
            order={order}
            orderBy={orderBy}
            onSelectAllClick={handleSelectAllClick}
            onRequestSort={handleRequestSort}
            rowCount={rowsPerPage}
            service={service}
          />
          {data.length > 0 ? (
            <ContentTable
              data={data}
              page={page}
              order={order}
              orderBy={orderBy}
              key={key}
              getRequests={getRequests}
              rowsPerPage={rowsPerPage}
              selected={selected}
              service={service}
              history={history}
              classes={classes}
              name={name}
            />
          ) : (
            <React.Fragment>
              {loading.principalLoading ? '' : <EmptyTable classes={classes} />}
            </React.Fragment>
          )}
        </Table>
      </div>

      <TablePagination
        component="div"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        colSpan={3}
        count={count}
        rowsPerPage={rowsPerPage}
        page={page}
        SelectProps={{ disabled: data.length === 0 }}
        labelRowsPerPage={''}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        ActionsComponent={TablePaginationActionsWrapped}
      />
    </Paper>
  );
};

EnhancedTable.propTypes = {
  classes: PropTypes.object.isRequired
};

export default withStyles(styles)(EnhancedTable);
